/**
 * product-model-slide.js
 *
 * Vases range only: bootstraps the interactive 3D model slide appended to the
 * product-hero carousel. Loaded as a module by product-hero.liquid only when
 * the product has the custom.vase_range metafield enabled AND a GLB model in
 * its media.
 *
 * three.js resolves through the import map printed by product-hero.liquid and
 * is dynamically imported on the FIRST activation of the model slide, so
 * users who never reach the slide download none of the 3D payload. If the
 * import or the GLB load fails, the slide keeps its small "Loading" text and
 * a retry runs on the next slide activation.
 *
 * Scene contract:
 *   - The renderer is transparent (alpha: true) — the slide background is
 *     owned by CSS (.carousel__slide--model → var(--colour-grey)), never JS.
 *   - The model is recentred on the origin after load, the camera orbits that
 *     origin, and OrbitControls panning is disabled, so the object is always
 *     centred in the viewport.
 *   - No auto-rotate anywhere: the model is static until the user
 *     deliberately drags it. Damping (inertia) is disabled while the
 *     reduce-motion preference is active.
 *   - While this slide is active, product-carousel.js hides its prev/next
 *     click zones — the slide is a standalone drag experience: pointer events
 *     land on the canvas (OrbitControls), never advance the carousel.
 *
 * Depends on:
 *   product-carousel.js — dispatches hattie:carousel-slide-change,
 *   hides its nav zones while the model slide is active
 */

const KEY_ROTATE_STEP = Math.PI / 24;
const KEY_ZOOM_FACTOR = 1.1;
const CAMERA_FIT_MARGIN = 1.2;

class ProductModelSlide {
  /**
   * @param {HTMLElement} slideEl — the [data-model-slide] element
   */
  constructor(slideEl) {
    this.slideEl = slideEl;
    this.viewport = slideEl.querySelector('[data-model-viewport]');
    this.src = this.viewport ? this.viewport.getAttribute('data-model-src') : '';
    this.loading = this.viewport
      ? this.viewport.querySelector('[data-model-loading]')
      : null;

    this._initRequested = false;
    this._ready = false;
    this._raf = null;

    this.THREE = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.controls = null;

    if (!this.viewport || !this.src) return;

    document.addEventListener('hattie:carousel-slide-change', (e) => {
      this._onSlideChange(e);
    });

    this.viewport.addEventListener('keydown', (e) => this._onKeydown(e));

    // Product with no images: the model slide is first and already active.
    if (this.slideEl.classList.contains('is-active')) {
      this._activate();
    }
  }

  _onSlideChange(e) {
    if (!e.detail) return;
    if (e.detail.slide === this.slideEl) {
      this._activate();
    } else {
      this._deactivate();
    }
  }

  _activate() {
    if (this._ready) {
      this._startLoop();
      return;
    }
    this._init();
  }

  _deactivate() {
    // Stop rendering while other slides are showing.
    this._stopLoop();
  }

  async _init() {
    if (this._initRequested) return;
    this._initRequested = true;

    try {
      const [THREE, { OrbitControls }, { GLTFLoader }] = await Promise.all([
        import('three'),
        import('three/addons/controls/OrbitControls.js'),
        import('three/addons/loaders/GLTFLoader.js'),
      ]);
      this.THREE = THREE;

      const gltf = await new GLTFLoader().loadAsync(this.src);
      this._buildScene(gltf.scene, OrbitControls);

      this._ready = true;
      // Once the scene is live the loading text is never needed again; it
      // only remains as the fallback when init fails.
      if (this.loading) {
        this.loading.remove();
        this.loading = null;
      }
      this.viewport.classList.add('is-loaded');

      // Only run the loop if the model slide is still the active one.
      if (this.slideEl.classList.contains('is-active')) {
        this._startLoop();
      }
    } catch (_) {
      // Degrade silently: the slide keeps showing the "Loading" text.
      // Allow a retry on the next slide activation (transient network etc).
      this._initRequested = false;
    }
  }

  /**
   * Basic scene: transparent renderer, hemisphere + key light, model centred
   * on the origin, camera fitted to the model's bounding sphere, and
   * OrbitControls orbiting the origin.
   */
  _buildScene(model, OrbitControls) {
    const THREE = this.THREE;

    // alpha: true + clear alpha 0 — the canvas is fully transparent; the
    // slide background is CSS-owned (var(--colour-grey)) and shows through.
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x888888, 3));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2);
    keyLight.position.set(3, 6, 4);
    this.scene.add(keyLight);

    // Recentre the model on the origin so orbiting always pivots around it
    // and it sits centred in the frame.
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    model.position.sub(center);
    this.scene.add(model);

    this.viewport.appendChild(this.renderer.domElement);

    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(this.viewport);
    this._resize();

    // Fit the camera distance so the whole bounding sphere is in frame on
    // both axes, with a margin.
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const radius = Math.max(sphere.radius, 0.001);
    const vFov = THREE.MathUtils.degToRad(this.camera.fov);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    const fitFov = Math.min(vFov, hFov);
    const distance = (radius / Math.sin(fitFov / 2)) * CAMERA_FIT_MARGIN;

    this.camera.near = distance / 100;
    this.camera.far = distance * 100;
    this.camera.position.set(0, 0, distance);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0);
    // Panning would move the model off-centre — the object stays centred.
    this.controls.enablePan = false;
    this.controls.minDistance = distance * 0.4;
    this.controls.maxDistance = distance * 2.5;
    this.controls.dampingFactor = 0.08;
    this.controls.update();

    this.renderer.render(this.scene, this.camera);
  }

  _startLoop() {
    if (this._raf !== null || !this._ready) return;
    const tick = () => {
      // Re-checked every frame so the accessibility modal's in-page
      // reduce-motion toggle takes effect immediately.
      this.controls.enableDamping = !this._reduceMotion();
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  _stopLoop() {
    if (this._raf !== null) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  }

  _reduceMotion() {
    if (typeof prefersReducedMotion === 'function') return prefersReducedMotion();
    return (
      document.documentElement.classList.contains('reduce-motion') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  _resize() {
    if (!this.renderer) return;
    const width = this.viewport.clientWidth;
    const height = this.viewport.clientHeight;
    if (!width || !height) return;

    // updateStyle: false — the canvas is sized by CSS (100% of the viewport).
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    // Keep the frame correct when resized while another slide is active.
    if (this._ready && this._raf === null) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  /**
   * Keyboard equivalent of drag-to-rotate and pinch-to-zoom, active while the
   * viewport is focused. product-carousel.js ignores arrow keys originating
   * inside [data-model-slide], so these never also change the slide.
   */
  _onKeydown(e) {
    if (!this._ready) return;

    let azimuth = 0;
    let polar = 0;
    let scale = 1;

    switch (e.key) {
      case 'ArrowLeft':
        azimuth = KEY_ROTATE_STEP;
        break;
      case 'ArrowRight':
        azimuth = -KEY_ROTATE_STEP;
        break;
      case 'ArrowUp':
        polar = -KEY_ROTATE_STEP;
        break;
      case 'ArrowDown':
        polar = KEY_ROTATE_STEP;
        break;
      case '+':
      case '=':
        scale = 1 / KEY_ZOOM_FACTOR;
        break;
      case '-':
      case '_':
        scale = KEY_ZOOM_FACTOR;
        break;
      default:
        return;
    }

    e.preventDefault();

    const THREE = this.THREE;
    const offset = this.camera.position.clone().sub(this.controls.target);
    const spherical = new THREE.Spherical().setFromVector3(offset);

    spherical.theta += azimuth;
    spherical.phi = THREE.MathUtils.clamp(
      spherical.phi + polar,
      0.05,
      Math.PI - 0.05
    );
    spherical.radius = THREE.MathUtils.clamp(
      spherical.radius * scale,
      this.controls.minDistance,
      this.controls.maxDistance
    );

    offset.setFromSpherical(spherical);
    this.camera.position.copy(this.controls.target).add(offset);
    this.camera.lookAt(this.controls.target);

    if (this._raf === null) {
      this.renderer.render(this.scene, this.camera);
    }
  }
}

function initProductModelSlides() {
  document.querySelectorAll('[data-model-slide]').forEach((el) => {
    if (!el.dataset.modelSlideInitialized) {
      el.dataset.modelSlideInitialized = 'true';
      new ProductModelSlide(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductModelSlides);
} else {
  initProductModelSlides();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initProductModelSlides);
}
